// To parse this data:
//
//   import { Convert, PlanscanSchema } from "./file";
//
//   const planscanSchema = Convert.toPlanscanSchema(json);
//
// These functions will throw an error if the JSON doesn't
// match the expected interface, even if the JSON is valid.

/**
 * Planscan Schema
 */
export interface PlanscanSchema {
    created_by:    number;
    date_created:  string;
    date_modified: string;
    id?:           number;
    modified_by:   number;
    data:          Data;
    }

export interface Data {
    activities:              number[];
    constraints:             number[];
    latestActivityNumber?:   number;
    latestConstraintNumber?: number;
    lock_mode?:              boolean;
    milestones:              number[];
    ppc_cache?:              PPCCache[];
    title:                   string;
    type:                    string;
    update_dates?:           UpdateDate[];
    }

export interface PPCCache {
    dates: Dates;
    ppc:   PPCCachePPC;
    }

export interface Dates {
    newer: string;
    older: string;
    }

export interface PPCCachePPC {
    company: Company[];
    project: Project;
    }

export interface Company {
    id:  number;
    ppc: CompanyPPC;
    }

export interface CompanyPPC {
    finished: ActivitiesCountData;
    started:  ActivitiesCountData;
    }

export interface ActivitiesCountData {
    early:    number;
    late:     number;
    missed:   number;
    on_time:  number;
    promised: number;
    }

export interface Project {
    finished: ActivitiesCountData;
    started:  ActivitiesCountData;
    }

export interface UpdateDate {
    timestamp: string;
    title?:    string;
    }

// Converts JSON strings to/from your types
// and asserts the results of JSON.parse at runtime
export class Convert {
    public static toPlanscanSchema(json: string): PlanscanSchema {
        return cast(JSON.parse(json), r("PlanscanSchema"));
    }

    public static planscanSchemaToJson(value: PlanscanSchema): string {
        return JSON.stringify(uncast(value, r("PlanscanSchema")), null, 2);
    }
}

function invalidValue(typ: any, val: any, key: any, parent: any = ''): never {
    const prettyTyp = prettyTypeName(typ);
    const parentText = parent ? ` on ${parent}` : '';
    const keyText = key ? ` for key "${key}"` : '';
    throw Error(`Invalid value${keyText}${parentText}. Expected ${prettyTyp} but got ${JSON.stringify(val)}`);
}

function prettyTypeName(typ: any): string {
    if (Array.isArray(typ)) {
        if (typ.length === 2 && typ[0] === undefined) {
            return `an optional ${prettyTypeName(typ[1])}`;
        } else {
            return `one of [${typ.map(a => { return prettyTypeName(a); }).join(", ")}]`;
        }
    } else if (typeof typ === "object" && typ.literal !== undefined) {
        return typ.literal;
    } else {
        return typeof typ;
    }
}

function jsonToJSProps(typ: any): any {
    if (typ.jsonToJS === undefined) {
        const map: any = {};
        typ.props.forEach((p: any) => map[p.json] = { key: p.js, typ: p.typ });
        typ.jsonToJS = map;
    }
    return typ.jsonToJS;
}

function jsToJSONProps(typ: any): any {
    if (typ.jsToJSON === undefined) {
        const map: any = {};
        typ.props.forEach((p: any) => map[p.js] = { key: p.json, typ: p.typ });
        typ.jsToJSON = map;
    }
    return typ.jsToJSON;
}

function transform(val: any, typ: any, getProps: any, key: any = '', parent: any = ''): any {
    function transformPrimitive(typ: string, val: any): any {
        if (typeof typ === typeof val) return val;
        return invalidValue(typ, val, key, parent);
    }

    function transformUnion(typs: any[], val: any): any {
        // val must validate against one typ in typs
        const l = typs.length;
        for (let i = 0; i < l; i++) {
            const typ = typs[i];
            try {
                return transform(val, typ, getProps);
            } catch (_) {}
        }
        return invalidValue(typs, val, key, parent);
    }

    function transformEnum(cases: string[], val: any): any {
        if (cases.indexOf(val) !== -1) return val;
        return invalidValue(cases.map(a => { return l(a); }), val, key, parent);
    }

    function transformArray(typ: any, val: any): any {
        // val must be an array with no invalid elements
        if (!Array.isArray(val)) return invalidValue(l("array"), val, key, parent);
        return val.map(el => transform(el, typ, getProps));
    }

    function transformDate(val: any): any {
        if (val === null) {
            return null;
        }
        const d = new Date(val);
        if (isNaN(d.valueOf())) {
            return invalidValue(l("Date"), val, key, parent);
        }
        return d;
    }

    function transformObject(props: { [k: string]: any }, additional: any, val: any): any {
        if (val === null || typeof val !== "object" || Array.isArray(val)) {
            return invalidValue(l(ref || "object"), val, key, parent);
        }
        const result: any = {};
        Object.getOwnPropertyNames(props).forEach(key => {
            const prop = props[key];
            const v = Object.prototype.hasOwnProperty.call(val, key) ? val[key] : undefined;
            result[prop.key] = transform(v, prop.typ, getProps, key, ref);
        });
        Object.getOwnPropertyNames(val).forEach(key => {
            if (!Object.prototype.hasOwnProperty.call(props, key)) {
                result[key] = transform(val[key], additional, getProps, key, ref);
            }
        });
        return result;
    }

    if (typ === "any") return val;
    if (typ === null) {
        if (val === null) return val;
        return invalidValue(typ, val, key, parent);
    }
    if (typ === false) return invalidValue(typ, val, key, parent);
    let ref: any = undefined;
    while (typeof typ === "object" && typ.ref !== undefined) {
        ref = typ.ref;
        typ = typeMap[typ.ref];
    }
    if (Array.isArray(typ)) return transformEnum(typ, val);
    if (typeof typ === "object") {
        return typ.hasOwnProperty("unionMembers") ? transformUnion(typ.unionMembers, val)
            : typ.hasOwnProperty("arrayItems")    ? transformArray(typ.arrayItems, val)
            : typ.hasOwnProperty("props")         ? transformObject(getProps(typ), typ.additional, val)
            : invalidValue(typ, val, key, parent);
    }
    // Numbers can be parsed by Date but shouldn't be.
    if (typ === Date && typeof val !== "number") return transformDate(val);
    return transformPrimitive(typ, val);
}

function cast<T>(val: any, typ: any): T {
    return transform(val, typ, jsonToJSProps);
}

function uncast<T>(val: T, typ: any): any {
    return transform(val, typ, jsToJSONProps);
}

function l(typ: any) {
    return { literal: typ };
}

function a(typ: any) {
    return { arrayItems: typ };
}

function u(...typs: any[]) {
    return { unionMembers: typs };
}

function o(props: any[], additional: any) {
    return { props, additional };
}

function m(additional: any) {
    return { props: [], additional };
}

function r(name: string) {
    return { ref: name };
}

const typeMap: any = {
    "PlanscanSchema": o([
        { json: "created_by", js: "created_by", typ: 0 },
        { json: "date_created", js: "date_created", typ: "" },
        { json: "date_modified", js: "date_modified", typ: "" },
        { json: "id", js: "id", typ: u(undefined, 0) },
        { json: "modified_by", js: "modified_by", typ: 0 },
        { json: "data", js: "data", typ: r("Data") },
    ], "any"),
    "Data": o([
        { json: "activities", js: "activities", typ: a(0) },
        { json: "constraints", js: "constraints", typ: a(0) },
        { json: "latestActivityNumber", js: "latestActivityNumber", typ: u(undefined, 0) },
        { json: "latestConstraintNumber", js: "latestConstraintNumber", typ: u(undefined, 0) },
        { json: "lock_mode", js: "lock_mode", typ: u(undefined, true) },
        { json: "milestones", js: "milestones", typ: a(0) },
        { json: "ppc_cache", js: "ppc_cache", typ: u(undefined, a(r("PPCCache"))) },
        { json: "title", js: "title", typ: "" },
        { json: "type", js: "type", typ: "" },
        { json: "update_dates", js: "update_dates", typ: u(undefined, a(r("UpdateDate"))) },
    ], "any"),
    "PPCCache": o([
        { json: "dates", js: "dates", typ: r("Dates") },
        { json: "ppc", js: "ppc", typ: r("PPCCachePPC") },
    ], "any"),
    "Dates": o([
        { json: "newer", js: "newer", typ: "" },
        { json: "older", js: "older", typ: "" },
    ], "any"),
    "PPCCachePPC": o([
        { json: "company", js: "company", typ: a(r("Company")) },
        { json: "project", js: "project", typ: r("Project") },
    ], "any"),
    "Company": o([
        { json: "id", js: "id", typ: 3.14 },
        { json: "ppc", js: "ppc", typ: r("CompanyPPC") },
    ], "any"),
    "CompanyPPC": o([
        { json: "finished", js: "finished", typ: r("ActivitiesCountData") },
        { json: "started", js: "started", typ: r("ActivitiesCountData") },
    ], "any"),
    "ActivitiesCountData": o([
        { json: "early", js: "early", typ: 3.14 },
        { json: "late", js: "late", typ: 3.14 },
        { json: "missed", js: "missed", typ: 3.14 },
        { json: "on_time", js: "on_time", typ: 3.14 },
        { json: "promised", js: "promised", typ: 3.14 },
    ], "any"),
    "Project": o([
        { json: "finished", js: "finished", typ: r("ActivitiesCountData") },
        { json: "started", js: "started", typ: r("ActivitiesCountData") },
    ], "any"),
    "UpdateDate": o([
        { json: "timestamp", js: "timestamp", typ: "" },
        { json: "title", js: "title", typ: u(undefined, "") },
    ], "any"),
};
export type PlanscanSchemaWithId = PlanscanSchema & RequiredId
